/**
 * 배포 후 stale 청크(content-hash 변경) 복구.
 *
 * 증상: 이미 열려 있던 탭이 배포 전 `index.html`(옛 청크명)을 들고 있다가 모듈을 lazy import
 *       하면, 서버엔 그 해시 파일(`CalendarModule-CXlkho-2.js` 등)이 더 이상 없어
 *       "Failed to fetch dynamically imported module" → 모듈 크래시 (2026-07-22 prod).
 *
 * 왜 "다시 시도"(ErrorBoundary state 리셋)로는 안 고쳐지나:
 *   React.lazy 는 실패한 import promise 를 캐시하고, 재렌더해도 여전히 옛 index.html 이 가리키는
 *   존재하지 않는 옛 청크를 또 요청한다 → 무한 실패. 오직 **하드 리로드**만 fresh index.html +
 *   새 해시명을 확보한다.
 *
 * 전략: 청크 로드 실패를 감지하면 1회 하드 리로드. 무한 리로드(예: SW 가 죽은 index.html 을
 *       계속 서빙)를 막기 위해 sessionStorage 타임스탬프로 "직전에 이미 리로드했으면" 억제한다.
 *       (탭 세션 동안만 유지되므로 다른 탭·다음 방문엔 영향 없음)
 */

const RELOAD_AT_KEY = "noten:chunk-reload-at";
/** 이 시간 안에 또 청크 실패면 리로드 루프로 간주 → 억제하고 폴백 UI 노출 */
const LOOP_WINDOW_MS = 10_000;

/** 청크(동적 import) 로드 실패 에러인가 — 브라우저별 메시지 편차 흡수 */
export function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error && error.name === "ChunkLoadError") return true;
  const msg =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  return (
    /Failed to fetch dynamically imported module/i.test(msg) || // Chrome/Edge
    /error loading dynamically imported module/i.test(msg) || // Firefox
    /Importing a module script failed/i.test(msg) || // Safari
    /ChunkLoadError/i.test(msg)
  );
}

/** 직전에 이미 리로드했나(=루프 위험) */
function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_AT_KEY) || 0);
    return at > 0 && Date.now() - at < LOOP_WINDOW_MS;
  } catch {
    return false; // private mode 등 — 억제 못 하면 최소한 1회는 시도됨
  }
}

/**
 * 청크 로드 에러면 1회 하드 리로드하고 `true`.
 * 청크 에러가 아니거나 방금 이미 리로드했으면(루프 방지) `false` → 호출자가 폴백 UI 를 띄운다.
 */
export function recoverFromChunkError(error: unknown): boolean {
  if (typeof window === "undefined") return false;
  if (!isChunkLoadError(error)) return false;
  if (reloadedRecently()) return false;
  try {
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    /* 저장 실패해도 리로드는 시도 */
  }
  window.location.reload();
  return true;
}

/**
 * 모듈 부팅 시 1회 — Vite 가 동적 import 실패 시 발생시키는 `vite:preloadError` 를 잡아
 * 하드 리로드한다. ErrorBoundary(React.lazy 렌더 실패 경로)보다 **먼저** 걸려, 폴백 UI 가
 * 번쩍이기 전에 조용히 복구한다. 부팅 시 라우트 프리로드(root 의 route glob) 실패도 커버.
 */
export function initChunkReload(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("vite:preloadError", (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    // 실제 리로드가 일어나면 preventDefault 로 throw 를 막는다.
    // 억제된 경우(루프 방지)엔 막지 않아 ErrorBoundary 가 폴백을 띄우게 둔다.
    if (recoverFromChunkError(payload)) {
      event.preventDefault();
    }
  });
}
